import React from 'react';
import { graphql } from 'react-relay';

import { css } from '@emotion/core';
import format from 'date-fns/format';

import BaseLayout from '@zego/layouts/BaseLayout';
import withData from '@zego/lib/withData';
import { defaultSocialImage } from '@zego/socialImages';
import {
  BREAKPOINTS,
  disabledColor,
  getColWidthCalc,
  unit,
} from '@zego/theme';

import H1 from '@zego/components/atoms/H1';
import RegularText from '@zego/components/atoms/RegularText';
import Spacer from '@zego/components/atoms/Spacer';
import ContentContainer from '@zego/components/ContentContainer';
import Head from '@zego/components/Head';

import BlogContent from './BlogContent';

const CANONICAL_BASE_URL = 'https://www.zego.com';

const BlogDetailPage = ({ viewer, query }) => (
  <BaseLayout
    viewer={viewer}
    headerIsInitiallyLightOnDark={false}
    unshiftHeader={true}
    title={viewer.post.title}
    description={viewer.post.meta.description || ''}
    ogImage={defaultSocialImage({
      slogan: viewer.post.title,
      buttonLabel: 'Read more',
      category: 'News',
    })}
    ogImageWidth={2048}
    ogImageHeight={1170}
  >
    <Head>
      <link
        rel="canonical"
        href={`${CANONICAL_BASE_URL}/blog/${query.slug}/`}
      />
      <meta name="author" content={viewer.post?.author?.name || 'Zego'} />x
    </Head>
    <ContentContainer>
      <article
        css={css`
          margin-bottom: ${unit(20)}px;
        `}
      >
        <div
          css={css`
            padding-top: ${unit(8)}px;
            padding-bottom: ${unit(4)}px;
            margin: 0 auto;
            ${BREAKPOINTS.medium} {
              width: ${getColWidthCalc(12, 20, 8)};
              padding-top: ${unit(15)}px;
              padding-bottom: ${unit(8)}px;
            }
          `}
        >
          <H1>{viewer.post.title}</H1>
          <Spacer height={unit(3)} />
          {viewer.post.author ? (
            <RegularText css={{ fontSize: 14, color: disabledColor(7) }}>
              Written by {viewer.post.author.name}
            </RegularText>
          ) : null}

          <RegularText css={{ fontSize: 14, color: disabledColor(7) }}>
            Published on{' '}
            <time
              dateTime={viewer.post.publicationDate}
              css={css`
                font-weight: bold;
              `}
            >
              {format(viewer.post.publicationDate, 'DD MMMM YYYY')}
            </time>
          </RegularText>
        </div>
        <BlogContent post={viewer.post} />
        <div
          css={css`
            clear: both;
          `}
        />
      </article>
    </ContentContainer>
  </BaseLayout>
);

export default withData(BlogDetailPage, {
  query: graphql`
    query BlogDetailPageQuery($country: String!, $slug: String!) {
      viewer {
        ...BaseLayout_viewer @arguments(country: $country)
        post: blogPostBySlug(slug: $slug) {
          ...BlogContent_post
          title
          author {
            name
          }
          publicationDate
          meta {
            description
          }
        }
      }
    }
  `,
  getVariables: ({ query }) => {
    return {
      country: query.country,
      slug: query.slug,
    };
  },
  getHeaders: ({ viewer }) => {
    // A post without publicationDate is unpublished and won't be shown
    if (!viewer.post?.publicationDate) {
      return [404];
    }
  },
});
